// DUX-03D: una vista de inventario para las cuatro entradas contextuales.
// Los casos son siempre los registros canónicos de pricing_follow_ups.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { backLabel } from '../../pricingJourney/navigation';
import PageHeader, { pageFrameSx } from '../../pricingJourney/components/PageHeader';
import {
  Alert, Box, Button, ButtonBase, Chip, CircularProgress, Collapse, Divider,
  LinearProgress, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  getMarginCenterCadenceAttention,
  getMarginCenterFollowUp,
  getMarginCenterFollowUpAttention,
  getMarginCenterFollowUpInventoryV2,
  addMarginCenterFollowUpNote,
  closeMarginCenterFollowUp,
  updateMarginCenterFollowUpPlanning,
} from '../services/marginCenterService';
// PRC-049: Seguimientos con origen, recuentos, tabla compacta y «Actualizar».
import FollowUpsTable, { ORIGIN_FILTERS, type FollowUpRowAction } from '../components/cadenceManagement/FollowUpsTable';
import FollowUpUpdateDialog, { recoveredConclusion, type FollowUpUpdateChoice, type FollowUpUpdateDecision } from '../components/cadenceManagement/FollowUpUpdateDialog';
import { useDeferredCadenceActions } from '../components/cadenceManagement/useDeferredCadenceActions';
import CustomerRelationshipFollowUpPanel from '../components/CustomerRelationshipFollowUpPanel';
import { getConsumptionObservations } from '../services/consumptionObservationService';
import { getEconomicPriceObservations } from '../services/economicPriceObservationService';
import CustomerRelationshipWorkspace from '../components/CustomerRelationshipWorkspace';
import ProaDatePicker from '../../../components/Common/ProaDatePicker';
import type {
  MarginCenterFollowUpInventoryV2Item,
  MarginCenterFollowUpInventoryView,
  MarginCenterFollowUpOrigin,
  MarginCenterInventoryContext,
  MarginCenterInventoryMatch,
  MarginCenterInventoryScope,
  MarginCenterInventorySort,
  MarginCenterInventoryStatus,
} from '../types/followUpInventory';
import type {
  MarginCenterComparisonMode,
  MarginCenterTimeGrain,
} from '../types/marginCenter';
import { toSpanishNumber } from '../../../modules/pricingJourney/formatNumber';

const CONTEXTS: MarginCenterInventoryContext[] = ['global', 'customer', 'product', 'relationship'];
const STATUSES: MarginCenterInventoryStatus[] = ['all', 'open', 'closed', 'new', 'in_review'];
const SCOPES: MarginCenterInventoryScope[] = ['all', 'customer', 'product', 'customer_product', 'transaction'];
const MATCHES: MarginCenterInventoryMatch[] = ['all', 'direct', 'contextual'];
const SORTS: MarginCenterInventorySort[] = ['updated_desc', 'created_desc'];
const ORIGINS: Array<MarginCenterFollowUpOrigin | 'all'> = ['all', 'cadence_purchase', 'price_change', 'tariff_update', 'manual'];
const VIEWS: MarginCenterFollowUpInventoryView[] = ['all', 'open', 'review_due', 'recovered', 'closed'];
// PRC-049: recuentos que filtran (boceto S4).
const VIEW_TABS: Array<{ id: MarginCenterFollowUpInventoryView; label: string; key: 'open_count' | 'review_due_count' | 'recovered_count' | 'closed_count' }> = [
  { id: 'open', label: 'Abiertos', key: 'open_count' },
  { id: 'review_due', label: 'Revisión vencida o para hoy', key: 'review_due_count' },
  { id: 'recovered', label: 'Compra recuperada', key: 'recovered_count' },
  { id: 'closed', label: 'Cerrados', key: 'closed_count' },
];
const PAGE_SIZES = [10, 20, 50] as const;
const PLANNING_SUMMARY_PAGE_SIZE = 1;
const CADENCE_SUMMARY_PAGE_SIZE = 1;

const COMPARISON_MODES: MarginCenterComparisonMode[] = [
  'previous_period',
  'year_over_year',
  'custom',
  'none',
];

const TIME_GRAINS: MarginCenterTimeGrain[] = [
  'day',
  'week',
  'month',
];

function readPageSize(raw: string | null): number {
  const value = Number(raw);
  return value === 10 || value === 50 ? value : 20;
}

function selected<T extends string>(raw: string | null, allowed: T[], fallback: T): T {
  return raw && allowed.includes(raw as T) ? raw as T : fallback;
}
function when(value: string | null | undefined): string {
  if (!value) return 'No programada';

  const dateOnly =
    /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1];

  if (!dateOnly) return 'Fecha no disponible';

  const [year, month, day] =
    dateOnly.split('-').map(Number);

  const date = new Date(
    year,
    month - 1,
    day,
  );

  return Number.isNaN(date.getTime())
    ? 'Fecha no disponible'
    : date.toLocaleDateString(
      'es-ES',
      { dateStyle: 'medium' },
    );
}
function statusLabel(value: string): string {
  if (value === 'new') return 'Nuevo';
  if (value === 'in_review') return 'En revisión';
  if (value === 'closed') return 'Cerrado';
  return 'Registrado';
}
function scopeLabel(value: string): string {
  if (value === 'customer') return 'Cliente';
  if (value === 'product') return 'Producto';
  if (value === 'customer_product') return 'Relación cliente-producto';
  if (value === 'transaction') return 'Transacción';
  return 'Todos los ámbitos';
}
function dateToIso(value: string): string | undefined {
  if (!/^\d{4}-\d\d-\d\d$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function civilDate(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim() ?? '';

  return /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? normalized
    : null;
}

function safeReturnPath(path: string | null): string {
  return path?.startsWith('/margin-center/') && !path.startsWith('//')
    ? path : '/margin-center/overview';
}

export default function MarginCenterFollowUpInventoryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const context = selected(params.get('context'), CONTEXTS, 'global');
  const customerId = params.get('customerId')?.trim() ?? '';
  const productId = params.get('productId')?.trim() ?? '';
  const status = selected(params.get('status'), STATUSES, 'all');
  const scope = selected(params.get('scope'), SCOPES, 'all');
  const match = context === 'relationship'
    ? selected(params.get('match'), MATCHES, 'all') : 'all';
  const sort = selected(params.get('sort'), SORTS, 'updated_desc');
  const origin = selected(params.get('origin'), ORIGINS, 'all');
  const view = selected(params.get('view'), VIEWS, 'open');
  const search = params.get('search')?.trim() ?? '';
  const createdFrom = params.get('createdFrom') ?? '';
  const createdBefore = params.get('createdBefore') ?? '';
  const cursor = params.get('cursor') ?? '';
  const caseId = params.get('case') ?? '';
  const pageSize = readPageSize(params.get('limit'));
  const backPath = safeReturnPath(params.get('returnTo'));
  const caseReturnTo =
    params.get('caseReturnTo');

  const caseReturnPath =
    safeReturnPath(
      caseReturnTo,
    );

  const selectedRelationshipCustomerId =
    params.get('relationshipCustomer')?.trim()
    || null;

  const selectedRelationshipProductId =
    params.get('relationshipProduct')?.trim()
    || null;

  const selectedRelationshipFollowUpId =
    params.get('relationshipFollowUp')?.trim()
    || null;

  const selectedRelationshipCadenceOccurrence =
    params.get('relationshipCadenceOccurrence')?.trim()
    || null;

  const [draftSearch, setDraftSearch] = useState(search);
  const [cursorTrail, setCursorTrail] = useState<string[]>([]);
  const [advanced, setAdvanced] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [listScroll, setListScroll] = useState(0);

  useEffect(() => { setDraftSearch(search); }, [search]);
  useEffect(() => { setHistoryOpen(false); }, [caseId]);
  useEffect(() => {
    if (
      !caseId
      && !selectedRelationshipCustomerId
      && !selectedRelationshipProductId
      && listScroll > 0
    ) {
      window.scrollTo({
        top: listScroll,
        behavior: 'auto',
      });
    }
  }, [
    caseId,
    listScroll,
    selectedRelationshipCustomerId,
    selectedRelationshipProductId,
  ]);

  const validContext = context === 'global'
    || (context === 'customer' && Boolean(customerId) && !productId)
    || (context === 'product' && Boolean(productId) && !customerId)
    || (context === 'relationship' && Boolean(customerId) && Boolean(productId));
  const filter = useMemo(() => ({
    context, customer_id: customerId || undefined, product_id: productId || undefined,
    status, scope, match, search: search || undefined, sort,
    created_from: dateToIso(createdFrom),
    created_before: dateToIso(createdBefore),
    limit: pageSize, cursor: cursor || undefined,
    origin, view,
  }), [context, customerId, productId, status, scope, match, search, sort, createdFrom, createdBefore, pageSize, cursor, origin, view]);
  const inventory = useQuery({
    queryKey: ['margin-center', 'follow-ups', 'inventory-v2', filter],
    queryFn: () => getMarginCenterFollowUpInventoryV2(filter),
    enabled: validContext && !caseId,
  });

  // PRC-049: acciones de fila. Cerrar y archivar, con «Deshacer»; reprogramar y
  // nota, al momento. Todo queda en la cronología del expediente.
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const deferredFollowUps = useDeferredCadenceActions();
  const [updating, setUpdating] = useState<{ item: MarginCenterFollowUpInventoryV2Item; choice: FollowUpUpdateChoice } | null>(null);
  const followUpLabel = (item: MarginCenterFollowUpInventoryV2Item) =>
    `${item.customer_label || item.customer_id || ''}${item.product_id ? ` · ${item.product_id}` : ''}`;
  const scheduleClose = (item: MarginCenterFollowUpInventoryV2Item, message: string, body: Parameters<typeof closeMarginCenterFollowUp>[1]) => {
    deferredFollowUps.schedule({
      key: item.id,
      message,
      // Recuentos: «pending» = abierto; «in_follow_up» = abierto con compra recuperada.
      from: item.recovered_on ? 'in_follow_up' : 'pending',
      to: 'resolved',
      run: () => closeMarginCenterFollowUp(item.id, body),
    });
  };
  const immediateUpdate = useMutation({
    mutationFn: ({ item, decision }: { item: MarginCenterFollowUpInventoryV2Item; decision: FollowUpUpdateDecision }) => {
      if (decision.kind === 'reschedule') {
        return updateMarginCenterFollowUpPlanning(item.id, {
          expected_updated_at: item.updated_at,
          next_action: item.next_action,
          next_review_at: decision.nextReviewIso,
        });
      }
      if (decision.kind === 'note') return addMarginCenterFollowUpNote(item.id, { content: decision.content });
      throw new Error('Operación no válida.');
    },
    onSuccess: (_result, { item, decision }) => {
      enqueueSnackbar(decision.kind === 'reschedule' ? `Revisión reprogramada: ${followUpLabel(item)}` : `Nota añadida: ${followUpLabel(item)}`, { variant: 'success' });
      // Lista, Revisiones programadas y campana.
      void queryClient.invalidateQueries({ queryKey: ['margin-center', 'follow-ups'] });
    },
    onError: (error: unknown) => {
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      enqueueSnackbar(typeof detail === 'string' && detail ? detail : 'No se pudo guardar. Inténtalo de nuevo.', { variant: 'error' });
    },
  });
  const onFollowUpAction = (item: MarginCenterFollowUpInventoryV2Item, action: FollowUpRowAction) => {
    if (action === 'open' || action === 'assess') { showCase(item.id); return; }
    if (action === 'close_recovered') {
      scheduleClose(item, `Seguimiento cerrado: ${followUpLabel(item)}`, {
        close_reason: 'explained', resolution_type: 'Compra recuperada', conclusion: recoveredConclusion(item),
      });
      return;
    }
    setUpdating({ item, choice: item.recovered_on ? 'recovered' : 'reschedule' });
  };
  const onFollowUpUpdate = (item: MarginCenterFollowUpInventoryV2Item, decision: FollowUpUpdateDecision) => {
    setUpdating(null);
    if (decision.kind === 'recovered') {
      scheduleClose(item, `Seguimiento cerrado: ${followUpLabel(item)}`, {
        close_reason: 'explained', resolution_type: 'Compra recuperada', conclusion: decision.conclusion,
      });
    } else if (decision.kind === 'cause') {
      scheduleClose(item, `${followUpLabel(item)} archivada.`, {
        close_reason: 'explained', resolution_type: decision.reason, conclusion: decision.conclusion,
      });
    } else {
      immediateUpdate.mutate({ item, decision });
    }
  };
  const inventorySummary = inventory.data?.summary ?? null;
  const visibleFollowUps = (inventory.data?.items ?? []).filter((item) => !deferredFollowUps.pendingKeys.has(item.id));
  const pendingClosedCount = deferredFollowUps.pending.length;
  const viewCount = (tab: (typeof VIEW_TABS)[number]) => {
    if (!inventorySummary) return null;
    const base = inventorySummary[tab.key];
    if (tab.id === 'closed') return base + pendingClosedCount;
    if (tab.id === 'open') return Math.max(0, base - pendingClosedCount);
    if (tab.id === 'recovered') return Math.max(0, base - deferredFollowUps.pending.filter((a) => a.from === 'in_follow_up').length);
    return base;
  };
  const detail = useQuery({
    queryKey: ['margin-center', 'follow-ups', 'detail', caseId],
    queryFn: () => getMarginCenterFollowUp(caseId),
    enabled: Boolean(caseId),
  });
  const attention = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'attention',
      'planning-summary',
      PLANNING_SUMMARY_PAGE_SIZE,
    ],
    queryFn: () => getMarginCenterFollowUpAttention({
      state: 'all',
      horizon_days: 7,
      limit: PLANNING_SUMMARY_PAGE_SIZE,
    }),
    enabled: validContext && context === 'global' && !caseId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  // 01B-07A: selección automática de intervalos por relación.
  // Lectura de cartera set-based; no se deduce una alerta confirmada.
  const consumptionObservations = useQuery({
    queryKey: ['margin-center', 'consumption-observations', 'hub', 'auto-1.2'],
    queryFn: () => getConsumptionObservations({ limit: 1 }),
    enabled: validContext && context === 'global' && !caseId,
    staleTime: 30 * 1000,
    retry: 1,
  });

  // ECON-ATTN-01A: solo se cuentan candidatas reales, nunca una cifra ficticia.
  const economicPriceObservations = useQuery({
    queryKey: ['margin-center', 'economic-price', 'hub', '1.2'],
    queryFn: () => getEconomicPriceObservations({ limit: 1 }),
    enabled: validContext && context === 'global' && !caseId,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const cadenceAttention = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'inventory-summary',
      CADENCE_SUMMARY_PAGE_SIZE,
    ],
    queryFn: () => getMarginCenterCadenceAttention({
      lifecycle: 'active_attention',
      limit: CADENCE_SUMMARY_PAGE_SIZE,
    }),
    enabled:
      validContext
      && context === 'global'
      && !caseId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    // PRC-049: el filtro Estado y los recuentos no se combinan: elegir uno quita el otro.
    if (key === 'status') { if (value && value !== 'all') next.set('view', 'all'); else next.delete('view'); }
    if (key === 'view') next.delete('status');
    next.delete('case'); next.delete('cursor');
    setCursorTrail([]);
    setParams(next);
  };
  const showCase = (id: string) => {
    setListScroll(window.scrollY);
    const next = new URLSearchParams(params);
    next.set('case', id);
    next.delete('caseReturnTo');
    setParams(next);
    window.scrollTo({ top: 0 });
  };
  const returnToList = () => {
    const next = new URLSearchParams(params);
    next.delete('case');
    next.delete('caseReturnTo');
    setParams(next);
  };
  const nextPage = () => {
    const nextCursor = inventory.data?.page.next_cursor;
    if (!nextCursor) return;
    setCursorTrail((current) => [...current, cursor]);
    const next = new URLSearchParams(params);
    next.set('cursor', nextCursor);
    setParams(next);
    window.scrollTo({ top: 0 });
  };
  const previousPage = () => {
    if (cursorTrail.length === 0) return;
    const previous = cursorTrail[cursorTrail.length - 1];
    setCursorTrail((current) => current.slice(0, -1));
    const next = new URLSearchParams(params);
    if (previous) next.set('cursor', previous); else next.delete('cursor');
    setParams(next);
    window.scrollTo({ top: 0 });
  };
  const openCadencePage = () => {
    const returnTo =
      `/margin-center/control-comercial${
        params.toString()
          ? `?${params.toString()}`
          : ''
      }`;

    const next =
      new URLSearchParams();

    next.set(
      'lifecycle',
      'active_attention',
    );

    next.set(
      'returnTo',
      returnTo,
    );

    [
      'from',
      'to',
      'comparison',
      'comparisonFrom',
      'comparisonTo',
      'grain',
    ].forEach((key) => {
      const value =
        params.get(key);

      if (value) {
        next.set(
          key,
          value,
        );
      }
    });

    navigate(
      `/margin-center/control-comercial/avisos/cadencia?${
        next.toString()
      }`,
    );
  };


  // D-073: the hub links to a specialized consumption surface.
  const openConsumptionPage = () => {
    const returnTo = `/margin-center/control-comercial${params.toString() ? `?${params.toString()}` : ''}`;
    const next = new URLSearchParams();
    next.set('returnTo', returnTo);
    ['from', 'to', 'comparison', 'comparisonFrom', 'comparisonTo', 'grain'].forEach(key => {
      const val = params.get(key);
      if (val) next.set(key, val);
    });
    navigate(`/margin-center/control-comercial/avisos/consumo?${next.toString()}`);
  };

  const openEconomicPricePage = () => {
    const returnTo = `/margin-center/control-comercial${params.toString() ? `?${params.toString()}` : ''}`;
    const next = new URLSearchParams();
    next.set('returnTo', returnTo);
    ['from', 'to', 'comparison', 'comparisonFrom', 'comparisonTo', 'grain'].forEach(key => {
      const val = params.get(key);
      if (val) next.set(key, val);
    });
    navigate(`/margin-center/control-comercial/avisos/condiciones-economicas?${next.toString()}`);
  };

  const openPlanningPage = () => {
    const returnTo =
      `/margin-center/control-comercial${
        params.toString()
          ? `?${params.toString()}`
          : ''
      }`;

    const next =
      new URLSearchParams();

    next.set(
      'state',
      'all',
    );

    next.set(
      'returnTo',
      returnTo,
    );

    navigate(
      `/margin-center/control-comercial/planificacion?${
        next.toString()
      }`,
    );
  };


  const selectRelationshipFollowUp = (
    id: string,
  ) => {
    const next =
      new URLSearchParams(params);

    next.set(
      'relationshipFollowUp',
      id,
    );

    setParams(
      next,
      { replace: true },
    );
  };

  const closeRelationshipWorkspace = () => {
    const next =
      new URLSearchParams(params);

    next.delete('relationshipCustomer');
    next.delete('relationshipProduct');
    next.delete('relationshipFollowUp');
    next.delete('relationshipCadenceOccurrence');
    next.delete('relationshipReturnTo');

    setParams(
      next,
      { replace: true },
    );
  };

  const currentCase = detail.data?.follow_up;
  const opening = detail.data?.events.find((e) =>
    e.event_type === 'note_added' && e.payload.note_kind === 'opening_reason'
    && typeof e.payload.content === 'string'
  );
  const openingReason = typeof opening?.payload.content === 'string'
    ? opening.payload.content : 'Motivo de apertura no registrado';
  const contextTitle = context === 'global' ? 'Control Comercial'
    : context === 'customer' ? 'Control Comercial · Cliente'
      : context === 'product' ? 'Control Comercial · Producto'
        : 'Control Comercial · Relación';
  const contextSummary = context === 'global'
    ? 'Expedientes abiertos y cerrados.'
    : context === 'customer'
      ? `Cliente ${customerId}`
      : context === 'product'
        ? `Producto ${productId}`
        : `${customerId} × ${productId}`;
  const pageLabel = cursor && cursorTrail.length === 0
    ? 'Página actual'
    : `Página ${cursorTrail.length + 1}`;

  const cadenceActiveCount =
    cadenceAttention.data?.summary.active_count ?? 0;

  const cadenceHistoricalCount =
    cadenceAttention.data?.summary.historical_unresolved_count ?? 0;

  const cadenceResolvedCount =
    cadenceAttention.data?.summary.resolved_count ?? 0;

  const planningItem =
    attention.data?.items[0] ?? null;

  const planningDueCount =
    attention.data?.summary.due_count ?? 0;

  const planningUpcomingCount =
    attention.data?.summary.upcoming_count ?? 0;

  const relationshipDataCutoffDate =
    civilDate(
      cadenceAttention.data?.data_cutoff_date,
    );

  const requestedRelationshipComparisonMode =
    selected(
      params.get('comparison'),
      COMPARISON_MODES,
      'year_over_year',
    );

  const requestedComparisonStartDate =
    civilDate(
      params.get('comparisonFrom'),
    );

  const requestedComparisonEndDate =
    civilDate(
      params.get('comparisonTo'),
    );

  const relationshipComparisonMode =
    requestedRelationshipComparisonMode === 'custom'
    && (
      !requestedComparisonStartDate
      || !requestedComparisonEndDate
    )
      ? 'year_over_year'
      : requestedRelationshipComparisonMode;

  const relationshipStartDate =
    civilDate(
      params.get('from'),
    )
    ?? (
      relationshipDataCutoffDate
        ? `${
          relationshipDataCutoffDate.slice(0, 4)
        }-01-01`
        : null
    );

  const relationshipEndDate =
    civilDate(
      params.get('to'),
    )
    ?? relationshipDataCutoffDate;

  const relationshipComparisonStartDate =
    relationshipComparisonMode === 'custom'
      ? requestedComparisonStartDate
      : null;

  const relationshipComparisonEndDate =
    relationshipComparisonMode === 'custom'
      ? requestedComparisonEndDate
      : null;

  const relationshipTimeGrain =
    selected(
      params.get('grain'),
      TIME_GRAINS,
      'week',
    );

  // DUX-03F: densidad media y paginación visible.
  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={1.5}>
        {/* PRC-032: cabecera común; «Volver» nombra su destino real (PRC-028). */}
        <PageHeader
          eyebrow="Centro de decisión · Control comercial"
          title={caseId ? 'Expediente comercial' : contextTitle}
          description={caseId ? 'Ficha única del expediente y su cronología.' : contextSummary}
          back={{
            label: caseId
              ? caseReturnTo
                ? backLabel(caseReturnPath)
                : 'Volver a Control comercial'
              : backLabel(backPath),
            onClick: () => {
              if (!caseId) {
                navigate(backPath);
                return;
              }

              if (caseReturnTo) {
                navigate(caseReturnPath);
                return;
              }

              returnToList();
            },
          }}
          sx={{ mb: 0 }}
          actions={!caseId && context !== 'global' ? (
                <Button
                  variant="text"
                  size="small"
                  sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set('context', 'global');
                    next.delete('customerId');
                    next.delete('productId');
                    next.delete('match');
                    next.delete('cursor');
                    next.delete('scope');
                    setCursorTrail([]);
                    setParams(next);
                  }}
                >
                  Inventario global
                </Button>
          ) : undefined}
        />

        {!validContext && !caseId && (
          <Alert severity="warning">
            El contexto solicitado está incompleto; vuelve a Cliente, Producto o Relación y abre Control Comercial desde allí.
          </Alert>
        )}

        {caseId ? (
          <>
            {detail.isPending && <CircularProgress size={22} />}
            {detail.isError && (
              <Alert severity="warning">No se pudo recuperar el expediente en la cuenta actual.</Alert>
            )}
            {detail.isSuccess && !currentCase && (
              <Alert severity="info">No se encontró el expediente solicitado.</Alert>
            )}
            {currentCase && (
              <>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip label={scopeLabel(currentCase.scope)} variant="outlined" size="small" />
                  <Chip
                    label={statusLabel(currentCase.status)}
                    color={currentCase.status === 'closed' ? 'default' : 'info'}
                    size="small"
                  />
                  <Chip label={`Abierto: ${when(currentCase.created_at)}`} size="small" variant="outlined" />
                </Stack>
                {currentCase.scope === 'customer_product' && currentCase.customer_id && currentCase.product_id ? (
                  <CustomerRelationshipFollowUpPanel
                    key={currentCase.id}
                    customerId={currentCase.customer_id}
                    productId={currentCase.product_id}
                    initialFollowUpId={currentCase.id}
                  />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.25,
                      bgcolor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: 2.5,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="overline" sx={{ color: '#38bdf8' }}>
                          Situación investigada
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: '#f8fafc', fontWeight: 800, whiteSpace: 'pre-wrap' }}
                        >
                          {openingReason}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {currentCase.customer_id && <Chip size="small" label={`Cliente ${currentCase.customer_id}`} />}
                        {currentCase.product_id && <Chip size="small" label={`Producto ${currentCase.product_id}`} />}
                      </Stack>
                      <Alert severity="info">
                        Lectura histórica del expediente. La edición de ámbitos generales aún no dispone de contrato operativo; no se inventan asociaciones ni formularios.
                      </Alert>
                      <Divider />
                      <Typography variant="subtitle1" fontWeight={800}>Próxima actuación</Typography>
                      <Typography variant="body2" sx={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                        {currentCase.next_action || 'No definida'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Revisión: {when(currentCase.next_review_at)}
                      </Typography>
                      {currentCase.conclusion && (
                        <Typography variant="body2" sx={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                          Conclusión registrada: {currentCase.conclusion}
                        </Typography>
                      )}
                      <Button
                        variant="text"
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                        onClick={() => setHistoryOpen((v) => !v)}
                      >
                        {historyOpen ? 'Ocultar cronología' : `Ver cronología (${detail.data?.events.length ?? 0})`}
                      </Button>
                      <Collapse in={historyOpen} unmountOnExit>
                        <Stack spacing={1}>
                          {(detail.data?.events ?? []).map((event) => (
                            <Box key={event.id} sx={{ p: 1.25, bgcolor: '#1e293b', borderRadius: 1.5 }}>
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                {event.event_type} · {when(event.created_at)}
                              </Typography>
                              {typeof event.payload.content === 'string' && (
                                <Typography variant="body2" sx={{ color: '#f8fafc', whiteSpace: 'pre-wrap' }}>
                                  {event.payload.content}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Paper>
                )}
              </>
            )}
          </>
        ) : validContext && (
          <>
            {context === 'global' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 3fr) minmax(260px, 1fr)',
                  },
                  gap: 1.25,
                  alignItems: 'stretch',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    minHeight: { lg: 224 },
                    height: '100%',
                    p: { xs: 1.25, md: 1.4 },
                    border:
                      '1px solid rgba(56,189,248,0.22)',
                    bgcolor:
                      'rgba(8,47,73,0.08)',
                    borderRadius: 2.25,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    spacing={1}
                    sx={{ height: '100%' }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#7dd3fc',
                            fontWeight: 850,
                            letterSpacing: 0.8,
                            lineHeight: 1.2,
                          }}
                        >
                          Avisos comerciales
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                            mt: 0.05,
                          }}
                        >
                          Situaciones que requieren atención
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            display: 'block',
                            mt: 0.15,
                          }}
                        >
                          Cada familia conserva su contrato y abre su
                          espacio de trabajo especializado.
                        </Typography>
                      </Box>

                      {cadenceAttention.data?.data_cutoff_date && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Datos hasta ${
                            when(
                              cadenceAttention.data.data_cutoff_date,
                            )
                          }`}
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </Stack>

                    {cadenceAttention.isFetching && (
                      <LinearProgress />
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flex: 1,
                        minHeight: 0,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pb: 0.25,
                        scrollbarWidth: 'thin',
                      }}
                    >
                      {/* CONTROL-HUB-02A:
                          cada familia ocupa 1/3 del 75 % disponible.
                          Nuevas familias se añaden lateralmente y no
                          incrementan la altura del hub. */}
                      <ButtonBase
                        onClick={openCadencePage}
                        sx={{
                          flex: {
                            xs: '0 0 86%',
                            sm: '0 0 48%',
                            lg:
                              '0 0 calc((100% - 16px) / 3)',
                          },
                          minWidth: {
                            xs: 230,
                            lg: 0,
                          },
                          alignSelf: 'stretch',
                          display: 'block',
                          textAlign: 'left',
                          p: 1.15,
                          borderRadius: 1.8,
                          border:
                            '1px solid rgba(56,189,248,0.24)',
                          bgcolor:
                            'rgba(2,6,23,0.28)',
                          '&:hover': {
                            bgcolor:
                              'rgba(8,47,73,0.22)',
                          },
                          '&:focus-visible': {
                            outline:
                              '2px solid #38bdf8',
                            outlineOffset: -2,
                          },
                        }}
                      >
                        <Stack
                          spacing={0.85}
                          sx={{ height: '100%' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#7dd3fc',
                                fontWeight: 850,
                                letterSpacing: 0.55,
                              }}
                            >
                              CADENCIA
                            </Typography>

                            <Typography
                              variant="h4"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                                mt: 0.1,
                                lineHeight: 1.05,
                                fontVariantNumeric:
                                  'tabular-nums',
                              }}
                            >
                              {cadenceAttention.isError
                                ? '—'
                                : cadenceAttention.isPending
                                  ? '…'
                                  : toSpanishNumber(cadenceActiveCount)}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                display: 'block',
                                mt: 0.15,
                              }}
                            >
                              avisos activos
                            </Typography>
                          </Box>

                          {cadenceAttention.isError
                            ? (
                              <Typography
                                variant="caption"
                                sx={{ color: '#fbbf24' }}
                              >
                                No disponible. No se muestra un falso cero.
                              </Typography>
                            )
                            : (
                              <Stack spacing={0.35}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#cbd5e1' }}
                                >
                                  {
                                    toSpanishNumber(cadenceHistoricalCount)
                                  } históricos sin resolver
                                  {' · '}
                                  {
                                    toSpanishNumber(cadenceResolvedCount)
                                  } archivados
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#38bdf8',
                                    fontWeight: 850,
                                  }}
                                >
                                  Ver casos →
                                </Typography>
                              </Stack>
                            )}
                        </Stack>
                      </ButtonBase>
                      {/* 01B-05A: detecciones, no avisos resueltos por episodio. */}
                      <ButtonBase
                        onClick={openConsumptionPage}
                        sx={{
                          flex: { xs: '0 0 86%', sm: '0 0 48%', lg: '0 0 calc((100% - 16px) / 3)' },
                          minWidth: { xs: 230, lg: 0 }, alignSelf: 'stretch',
                          display: 'block', textAlign: 'left', p: 1.15,
                          borderRadius: 1.8, border: '1px solid rgba(56,189,248,0.24)',
                          bgcolor: 'rgba(2,6,23,0.28)',
                          '&:hover': { bgcolor: 'rgba(8,47,73,0.22)' },
                          '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: -2 },
                        }}
                      >
                        <Stack spacing={0.75} sx={{ height: '100%' }} justifyContent="space-between">
                          <Box>
                            <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 850, letterSpacing: 0.55 }}>CONSUMO · OBSERVACIONES · DETECCIÓN AUTOMÁTICA</Typography>
                            <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.1, lineHeight: 1.05 }}>
                              {consumptionObservations.isPending ? '…' : consumptionObservations.isError ? '—' : consumptionObservations.data?.selection.horizon_mode === 'automatic_per_relationship' ? toSpanishNumber((consumptionObservations.data.summary.detected_count ?? 0)) : '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>situaciones candidatas detectadas</Typography>
                          </Box>
                          <Box>
                            {consumptionObservations.isError ? (
                              <Typography variant="caption" sx={{ color: '#fbbf24' }}>No disponible. No se muestra un falso cero.</Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                Con compras, no comparables: {toSpanishNumber((consumptionObservations.data?.summary.limited_or_invalid_count ?? 0))} relaciones
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ display: 'block', color: '#38bdf8', fontWeight: 850, mt: 0.35 }}>Ver detecciones →</Typography>
                          </Box>
                        </Stack>
                      </ButtonBase>
                       {/* ECON-ATTN-01A: solo observaciones de precio; margen pendiente. */}
                       <ButtonBase
                         onClick={openEconomicPricePage}
                         sx={{
                           flex: { xs: '0 0 86%', sm: '0 0 48%', lg: '0 0 calc((100% - 16px) / 3)' },
                           minWidth: { xs: 230, lg: 0 }, alignSelf: 'stretch',
                           display: 'block', textAlign: 'left', p: 1.15,
                           borderRadius: 1.8, border: '1px solid rgba(56,189,248,0.24)',
                           bgcolor: 'rgba(2,6,23,0.28)',
                           '&:hover': { bgcolor: 'rgba(8,47,73,0.22)' },
                           '&:focus-visible': { outline: '2px solid #38bdf8', outlineOffset: -2 },
                         }}
                       >
                         <Stack spacing={0.75} sx={{ height: '100%' }} justifyContent="space-between">
                           <Box>
                             <Typography variant="caption" sx={{ color: '#7dd3fc', fontWeight: 850, letterSpacing: 0.55 }}>
                               CONDICIONES ECONÓMICAS · PRECIO NETO
                             </Typography>
                             <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.1, lineHeight: 1.05 }}>
                               {economicPriceObservations.isPending || economicPriceObservations.isError
                                 || economicPriceObservations.data?.version !== '1.2'
                                 ? '—' : toSpanishNumber((economicPriceObservations.data.summary.detected_count ?? 0))}
                             </Typography>
                             <Typography variant="caption" sx={{ color: '#94a3b8' }}>observaciones candidatas de precio</Typography>
                           </Box>
                           <Box>
                             {economicPriceObservations.isError || (economicPriceObservations.data && economicPriceObservations.data.version !== '1.2')
                               ? <Typography variant="caption" sx={{ color: '#fbbf24' }}>No disponible. No se muestra un falso cero.</Typography>
                               : <Typography variant="caption" sx={{ color: '#cbd5e1' }}>Relaciones comparables: {toSpanishNumber((economicPriceObservations.data?.summary.relationships_comparable ?? 0))}</Typography>}
                             <Typography variant="caption" sx={{ display: 'block', color: '#38bdf8', fontWeight: 850, mt: 0.35 }}>Ver observaciones →</Typography>
                           </Box>
                         </Stack>
                       </ButtonBase>

                    </Box>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    minHeight: { lg: 224 },
                    height: '100%',
                    p: { xs: 1.25, md: 1.4 },
                    border:
                      planningDueCount > 0
                        ? '1px solid rgba(245,158,11,0.28)'
                        : '1px solid rgba(148,163,184,0.18)',
                    bgcolor:
                      planningDueCount > 0
                        ? 'rgba(120,53,15,0.08)'
                        : 'rgba(15,23,42,0.58)',
                    borderRadius: 2.25,
                  }}
                >
                  <Stack
                    spacing={1}
                    sx={{ height: '100%' }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                      >
                        <CalendarMonthRoundedIcon
                          fontSize="small"
                          sx={{
                            color:
                              planningDueCount > 0
                                ? '#fbbf24'
                                : '#7dd3fc',
                          }}
                        />

                        <Typography
                          variant="overline"
                          sx={{
                            color:
                              planningDueCount > 0
                                ? '#fbbf24'
                                : '#7dd3fc',
                            fontWeight: 850,
                            letterSpacing: 0.8,
                            lineHeight: 1.2,
                          }}
                        >
                          Revisiones programadas
                        </Typography>
                      </Stack>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                          display: 'block',
                          mt: 0.4,
                        }}
                      >
                        Próxima acción planificada.
                      </Typography>
                    </Box>

                    {attention.isFetching && (
                      <LinearProgress />
                    )}

                    {attention.isError
                      ? (
                        <Alert
                          severity="warning"
                          sx={{
                            py: 0.15,
                            '& .MuiAlert-message': {
                              py: 0.35,
                            },
                          }}
                        >
                          Revisiones programadas no disponibles.
                        </Alert>
                      )
                      : planningItem
                        ? (
                          <ButtonBase
                            onClick={() =>
                              showCase(
                                planningItem.follow_up_id,
                              )}
                            sx={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              p: 1,
                              borderRadius: 1.6,
                              border:
                                planningItem.attention_state
                                  === 'due'
                                  ? '1px solid rgba(245,158,11,0.26)'
                                  : '1px solid rgba(56,189,248,0.18)',
                              bgcolor:
                                'rgba(2,6,23,0.24)',
                              '&:hover': {
                                bgcolor:
                                  'rgba(30,41,59,0.55)',
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  planningItem.attention_state
                                    === 'due'
                                    ? '#fbbf24'
                                    : '#7dd3fc',
                                fontWeight: 850,
                              }}
                            >
                              {planningItem.attention_state
                                === 'due'
                                ? 'REVISIÓN VENCIDA'
                                : 'PRÓXIMA REVISIÓN'}
                            </Typography>

                            <Typography
                              variant="body1"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                                mt: 0.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {
                                planningItem.customer_label
                                || planningItem.customer_id
                                || 'Sin cliente'
                              }
                              {
                                planningItem.product_label
                                || planningItem.product_id
                                  ? ` · ${
                                    planningItem.product_label
                                    || planningItem.product_id
                                  }`
                                  : ''
                              }
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: '#cbd5e1',
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {planningItem.next_action
                                || 'Siguiente acción no definida'}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                display: 'block',
                                mt: 0.35,
                              }}
                            >
                              {when(
                                planningItem.next_review_at,
                              )}
                            </Typography>
                          </ButtonBase>
                        )
                        : (
                          <Typography
                            variant="body2"
                            sx={{ color: '#94a3b8' }}
                          >
                            No hay revisiones vencidas ni previstas
                            en los próximos 7 días.
                          </Typography>
                        )}

                    <Stack
                      direction="row"
                      spacing={0.6}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Vencidas · ${
                          attention.isError
                            ? '—'
                            : planningDueCount
                        }`}
                        sx={{
                          color:
                            planningDueCount > 0
                              ? '#fbbf24'
                              : undefined,
                        }}
                      />

                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Próximos 7 días · ${
                          attention.isError
                            ? '—'
                            : planningUpcomingCount
                        }`}
                      />
                    </Stack>

                    <Button
                      size="small"
                      variant="text"
                      onClick={openPlanningPage}
                      sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        px: 0,
                        minWidth: 0,
                        fontWeight: 850,
                      }}
                    >
                      Ver revisiones programadas →
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            )}

            <Paper
              elevation={0}
              sx={{
                border:
                  '1px solid rgba(148,163,184,0.18)',
                bgcolor:
                  'rgba(15,23,42,0.58)',
                borderRadius: 2.25,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: { xs: 1.25, md: 1.5 },
                }}
              >
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={0.75}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                      }}
                    >
                      Seguimientos en curso
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: '#94a3b8',
                        display: 'block',
                        mt: 0.1,
                      }}
                    >
                      Todo lo que se está gestionando, sea cual sea su origen.
                    </Typography>
                  </Box>

                  {inventory.data && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${
                        inventory.data.page.returned_count
                      } ${
                        inventory.data.page.returned_count === 1
                          ? 'expediente'
                          : 'expedientes'
                      } en esta página`}
                    />
                  )}
                </Stack>

                {/* PRC-049 (S4): recuentos que filtran y filtro Origen. */}
                <Box role="tablist" aria-label="Seguimientos" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: '10px' }}>
                  {VIEW_TABS.map((tab) => {
                    const on = view === tab.id;
                    const count = viewCount(tab);
                    return (
                      <ButtonBase
                        key={tab.id}
                        role="tab"
                        aria-selected={on}
                        onClick={() => change('view', tab.id === 'open' ? '' : tab.id)}
                        sx={{
                          border: '1px solid', borderColor: on ? '#38bdf8' : '#233149', borderRadius: '12px', p: '7px 12px',
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', textAlign: 'left',
                          bgcolor: on ? 'rgba(56,189,248,.08)' : 'transparent',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 12.5, color: on ? '#7dd3fc' : '#94a3b8' }}>{tab.label}</Box>
                        <Box component="b" sx={{ fontSize: 18, fontWeight: 800, color: tab.id === 'review_due' && count ? '#fcd34d' : '#f8fafc', fontVariantNumeric: 'tabular-nums' }}>
                          {count == null ? '—' : toSpanishNumber(count)}
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="caption" sx={{ color: '#94a3b8', mr: 0.5 }}>Origen</Typography>
                  {ORIGIN_FILTERS.map((item) => {
                    const on = origin === item.id;
                    return (
                      <ButtonBase
                        key={item.id}
                        aria-pressed={on}
                        onClick={() => change('origin', item.id === 'all' ? '' : item.id)}
                        sx={{
                          border: '1px solid', borderColor: on ? '#38bdf8' : '#334155', borderRadius: 999, px: '12px', py: '5px',
                          fontSize: 12.5, fontWeight: 600, color: on ? '#7dd3fc' : '#cbd5e1', bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
                        }}
                      >
                        {item.label}
                      </ButtonBase>
                    );
                  })}
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar por motivo, cliente o producto"
                    value={draftSearch}
                    inputProps={{ 'aria-label': 'Buscar expedientes', maxLength: 120 }}
                    onChange={(e) => setDraftSearch(e.target.value.slice(0, 120))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') change('search', draftSearch.trim());
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SearchRoundedIcon />}
                    onClick={() => change('search', draftSearch.trim())}
                    sx={{
                      minHeight: 40,
                      px: 2,
                      textTransform: 'none',
                      flexShrink: 0,
                    }}
                  >
                    Buscar
                  </Button>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <TextField
                    select
                    size="small"
                    label="Estado"
                    value={status}
                    onChange={(e) => change('status', e.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="open">Abiertos</MenuItem>
                    <MenuItem value="closed">Cerrados</MenuItem>
                    <MenuItem value="new">Nuevos</MenuItem>
                    <MenuItem value="in_review">En revisión</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Ámbito"
                    value={scope}
                    onChange={(e) => change('scope', e.target.value)}
                    sx={{ minWidth: 190 }}
                  >
                    {SCOPES.map((item) => (
                      <MenuItem key={item} value={item}>{scopeLabel(item)}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Orden"
                    value={sort}
                    onChange={(e) => change('sort', e.target.value)}
                    sx={{ minWidth: 170 }}
                  >
                    <MenuItem value="updated_desc">Última actualización</MenuItem>
                    <MenuItem value="created_desc">Fecha de apertura</MenuItem>
                  </TextField>

                  {context === 'relationship' && (
                    <TextField
                      select
                      size="small"
                      label="Tipo de caso"
                      value={match}
                      onChange={(e) => change('match', e.target.value)}
                      sx={{ minWidth: 190 }}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="direct">De esta relación</MenuItem>
                      <MenuItem value="contextual">Generales relacionados</MenuItem>
                    </TextField>
                  )}

                  <Button
                    size="small"
                    variant="text"
                    sx={{ minHeight: 40, textTransform: 'none', px: 1 }}
                    onClick={() => setAdvanced((v) => !v)}
                  >
                    {advanced ? 'Ocultar filtros' : 'Más filtros'}
                  </Button>
                </Stack>

                <Collapse in={advanced}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ pt: 0.25 }}
                  >
                    {context === 'global' && (
                      <>
                        <TextField
                          size="small"
                          label="Cliente"
                          value={customerId}
                          onChange={(e) => change('customerId', e.target.value)}
                        />
                        <TextField
                          size="small"
                          label="Producto"
                          value={productId}
                          onChange={(e) => change('productId', e.target.value)}
                        />
                      </>
                    )}
                    {/* PRC-032: selector de fechas común. */}
                    <ProaDatePicker
                      label="Abierto desde"
                      value={createdFrom}
                      onChange={(value) => change('createdFrom', value)}
                    />
                    <ProaDatePicker
                      label="Abierto antes de"
                      value={createdBefore}
                      onChange={(value) => change('createdBefore', value)}
                    />
                  </Stack>
                </Collapse>
              </Stack>
              </Box>

            {inventory.isPending && <LinearProgress />}
            {inventory.isError && (
              <Alert
                severity="error"
                action={(
                  <Button color="inherit" size="small" onClick={() => void inventory.refetch()}>
                    Reintentar
                  </Button>
                )}
              >
                No se pudo consultar el inventario. Se conserva la selección para reintentar.
              </Alert>
            )}
            {inventory.data?.status === 'empty' && (
              <Alert severity="info">
                No hay expedientes para esta selección.
              </Alert>
            )}

            {inventory.data && visibleFollowUps.length > 0 && (
              <Box sx={{ borderTop: '1px solid rgba(148,163,184,0.14)', px: { xs: 0.5, md: 1 }, py: 0.5 }}>
                <FollowUpsTable
                  items={visibleFollowUps}
                  today={inventorySummary?.today ?? null}
                  onAction={onFollowUpAction}
                />
              </Box>
            )}
            <FollowUpUpdateDialog
              item={updating?.item ?? null}
              initialChoice={updating?.choice}
              onClose={() => setUpdating(null)}
              onConfirm={onFollowUpUpdate}
            />

            {inventory.data && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.25}
                sx={{
                  minHeight: 42,
                  px: { xs: 1.25, md: 1.5 },
                  py: 0.9,
                  borderTop:
                    '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {pageLabel} · {inventory.data.page.returned_count}{' '}
                  {inventory.data.page.returned_count === 1 ? 'expediente' : 'expedientes'}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                >
                  <TextField
                    select
                    size="small"
                    label="Filas"
                    value={String(pageSize)}
                    onChange={(e) => change('limit', e.target.value)}
                    sx={{ width: 104 }}
                  >
                    {PAGE_SIZES.map((size) => (
                      <MenuItem key={size} value={String(size)}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>

                  {cursor && cursorTrail.length === 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => change('cursor', '')}
                    >
                      Inicio
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={cursorTrail.length === 0}
                    onClick={previousPage}
                  >
                    Anterior
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={!inventory.data.page.has_more || !inventory.data.page.next_cursor}
                    onClick={nextPage}
                  >
                    Siguiente
                  </Button>
                </Stack>
              </Stack>
            )}
            </Paper>
          </>
        )}

        {selectedRelationshipCustomerId
          && selectedRelationshipProductId
          && (
          <CustomerRelationshipWorkspace
            customerId={
              selectedRelationshipCustomerId
            }
            productId={
              selectedRelationshipProductId
            }
            startDate={
              relationshipStartDate
            }
            endDate={
              relationshipEndDate
            }
            comparisonMode={
              relationshipComparisonMode
            }
            comparisonStartDate={
              relationshipComparisonStartDate
            }
            comparisonEndDate={
              relationshipComparisonEndDate
            }
            timeGrain={
              relationshipTimeGrain
            }
            initialMode={
              selectedRelationshipCadenceOccurrence
                ? 'follow_up'
                : 'analysis'
            }
            initialFollowUpId={
              selectedRelationshipFollowUpId
            }
            cadenceAttentionOccurrenceDate={
              selectedRelationshipCadenceOccurrence
            }
            onFollowUpIdChange={
              selectRelationshipFollowUp
            }
            onClose={
              closeRelationshipWorkspace
            }
          />
        )}
      </Stack>
    </Box>
  );
}
